/**
 * Google Business photos via Places API (New) + Maps JavaScript Place class.
 * GCP: enable "Places API (New)" + Maps JavaScript API (classic Places API not required).
 */
(function () {
  'use strict';

  var DELAY_MS = 520;
  var failLogged = 0;
  var okCount = 0;
  var permissionBlocked = false;

  function isPermissionError(err) {
    if (!err) return false;
    var msg = String(err.message || err);
    return (
      err.code === 403 ||
      /403|PERMISSION_DENIED|API_KEY_SERVICE_BLOCKED|API_KEY_HTTP_REFERRER|forbidden/i.test(msg)
    );
  }

  function logPermissionHelp() {
    if (permissionBlocked) return;
    permissionBlocked = true;
    console.error(
      '[aioo-store-photos] 403 from places.googleapis.com — fix API key in GCP (Credentials):\n' +
        '  1) API restrictions: allow "Maps JavaScript API" AND "Places API (New)" (not only Maps JS).\n' +
        '  2) Application restrictions: HTTP referrers — https://www.aioostore.lv/* and https://aioostore.lv/*\n' +
        '  3) Billing enabled on project Aioostore.\n' +
        '  Test: temporarily set API restrictions to "Don\'t restrict key"; if photos load, re-add the two APIs above.'
    );
  }

  function logFail(label, storeName, err) {
    if (failLogged >= 5) return;
    failLogged++;
    var msg = err && (err.message || String(err));
    console.warn('[aioo-store-photos]', label, storeName || '', msg || '');
  }

  function shouldSkip(store) {
    if (!store) return true;
    if (store.image) return true;
    if (store._googlePhotoUrl || store.google_photo_url) return true;
    if (store.google_photo_name || store.google_photo_ref) return true;
    return false;
  }

  function unwrap(row) {
    return row && row.d ? row.d : row;
  }

  function distKm(lat1, lng1, lat2, lng2) {
    var r = Math.PI / 180;
    var dLat = (lat2 - lat1) * r;
    var dLng = (lng2 - lng1) * r;
    var h = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1 * r) * Math.cos(lat2 * r) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 12742 * Math.asin(Math.sqrt(h));
  }

  function latLngOf(loc) {
    if (!loc) return null;
    if (typeof loc.lat === 'function') return { lat: loc.lat(), lng: loc.lng() };
    if (typeof loc.latitude === 'number') return { lat: loc.latitude, lng: loc.longitude };
    if (typeof loc.lat === 'number') return { lat: loc.lat, lng: loc.lng };
    return null;
  }

  function displayNameText(place) {
    if (!place || !place.displayName) return '';
    var dn = place.displayName;
    if (typeof dn === 'string') return dn;
    return dn.text || '';
  }

  /** Prefer AIOO listing over parent mall / TC when several POIs share coordinates. */
  function placeScore(place, store, km) {
    var label = displayNameText(place).toLowerCase();
    var score = 0;
    if (/\baioo\b/.test(label)) score += 1000;
    var storeName = String((store && store.name) || '').toLowerCase();
    if (storeName && label.indexOf(storeName.replace(/^aioo\s+/, '')) >= 0) score += 120;
    if (/tirdzniecības centrs|shopping center|tirdzniecibas centrs/.test(label) && !/\baioo\b/.test(label)) {
      score -= 400;
    }
    return score - km * 5;
  }

  function pickBestPlace(places, lat, lng, store) {
    var best = null;
    var bestScore = -Infinity;
    var bestKm = null;
    for (var i = 0; i < places.length; i++) {
      var ll = latLngOf(places[i].location);
      if (!ll) continue;
      var km = distKm(lat, lng, ll.lat, ll.lng);
      if (km > 2.5) continue;
      var sc = placeScore(places[i], store, km);
      if (sc > bestScore || (sc === bestScore && bestKm !== null && km < bestKm)) {
        bestScore = sc;
        bestKm = km;
        best = places[i];
      }
    }
    return best;
  }

  function photoIndex(store) {
    var n = store && store.google_photo_index;
    if (n == null || n === '') return 0;
    var i = parseInt(n, 10);
    return isNaN(i) || i < 0 ? 0 : i;
  }

  function pickPhotoFromPlace(place, store) {
    var photos = place && place.photos;
    if (!photos || !photos.length) return null;
    var idx = photoIndex(store);
    if (idx >= photos.length) idx = 0;
    return photos[idx];
  }

  function placeIdForApi(raw) {
    if (!raw) return '';
    var pid = String(raw).trim();
    if (!pid) return '';
    return pid.indexOf('places/') === 0 ? pid : 'places/' + pid;
  }

  function applyPhoto(store, place, ph, onUpdate, row) {
    if (!ph || typeof ph.getURI !== 'function') return;
    store.google_place_id = place.id || place.placeId || '';
    if (ph.name) store.google_photo_name = ph.name;
    store._googlePhotoUrl = ph.getURI({ maxWidth: 640, maxHeight: 480 });
    var attrs = ph.authorAttributions || ph.html_attributions;
    if (attrs && attrs.length) {
      var a = attrs[0];
      store.google_photo_attr = a.displayName || a.uri || (typeof a === 'string' ? a : '');
    }
    okCount++;
    if (typeof onUpdate === 'function') onUpdate(row);
  }

  async function hydrateOne(Place, s, onUpdate, row) {
    var lat = Number(s.lat);
    var lng = Number(s.lng);
    var place = null;

    if (s.google_place_id) {
      place = new Place({ id: placeIdForApi(s.google_place_id) });
      await place.fetchFields({ fields: ['id', 'location', 'photos', 'displayName'] });
    } else {
      var textQuery = [s.name, s.store, 'Latvia'].filter(Boolean).join(' ');
      var resp = await Place.searchByText({
        textQuery: textQuery,
        fields: ['id', 'location', 'photos', 'displayName'],
        locationBias: { center: { lat: lat, lng: lng }, radius: 1200 },
        maxResultCount: 8,
        language: 'lv',
        region: 'lv',
      });
      var places = (resp && resp.places) || [];
      place = pickBestPlace(places, lat, lng, s);
      if (!place) return;
      if (!place.photos || !place.photos.length) {
        await place.fetchFields({ fields: ['photos'] });
      }
    }

    var ph = pickPhotoFromPlace(place, s);
    if (ph) applyPhoto(s, place, ph, onUpdate, row);
  }

  /**
   * @param {Array<{d: object}>|object[]} stores
   * @param {google.maps.Map} map - unused; kept for call-site compatibility
   * @param {(storeWrap: object) => void} [onUpdate]
   */
  window.aiooHydrateStoreGooglePhotos = function (stores, map, onUpdate) {
    if (!stores || !stores.length) return;
    if (!(window.google && google.maps && google.maps.importLibrary)) {
      console.warn('[aioo-store-photos] Maps JS importLibrary missing');
      return;
    }

    okCount = 0;
    failLogged = 0;
    permissionBlocked = false;
    try {
      window.AIOO_STORE_PHOTOS_STATUS = 'loading';
    } catch (e) { /* noop */ }
    var queue = stores.slice();
    var busy = false;

    google.maps
      .importLibrary('places')
      .then(function (lib) {
        var Place = lib.Place;
        if (!Place || typeof Place.searchByText !== 'function') {
          console.warn('[aioo-store-photos] Place.searchByText unavailable — enable Places API (New)');
          return;
        }

        function runNext() {
          if (permissionBlocked) {
            busy = false;
            return;
          }
          if (!queue.length) {
            busy = false;
            if (okCount > 0) {
              console.info('[aioo-store-photos] loaded', okCount, 'Google listing photos (Places API New)');
              try {
                window.AIOO_STORE_PHOTOS_STATUS = 'ok:' + okCount;
              } catch (e2) { /* noop */ }
            } else {
              console.warn(
                '[aioo-store-photos] no photos — enable Places API (New), billing, and key restrictions for Maps JS + Places (New)'
              );
              try {
                window.AIOO_STORE_PHOTOS_STATUS = permissionBlocked ? 'blocked' : 'empty';
              } catch (e3) { /* noop */ }
            }
            return;
          }
          busy = true;
          var row = queue.shift();
          var s = unwrap(row);
          if (shouldSkip(s)) {
            window.setTimeout(runNext, 0);
            return;
          }
          hydrateOne(Place, s, onUpdate, row)
            .catch(function (err) {
              logFail('searchByText', s.name, err);
              if (isPermissionError(err)) {
                logPermissionHelp();
                queue.length = 0;
              }
            })
            .finally(function () {
              window.setTimeout(runNext, DELAY_MS);
            });
        }

        if (!busy) runNext();
      })
      .catch(function (err) {
        console.warn('[aioo-store-photos] importLibrary(places) failed', err);
      });
  };
})();
